// SharedCampaignsList.js
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { auth } from "../../firebase";
import {
  FaUserCircle,
  FaShareAlt,
  FaLock,
  FaEdit,
  FaEye,
} from "react-icons/fa";
import FirebaseUtils from "../services/FirebaseUtils";

const SharedCampaignsList = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchSharedCampaigns = async () => {
      try {
        setLoading(true);
        const user = auth.currentUser;

        if (!user) {
          setError("You must be logged in to view shared campaigns");
          setLoading(false);
          return;
        }

        // Use FirebaseUtils to get all campaigns the user has access to
        const sharedCampaigns = await FirebaseUtils.getUserCampaigns(user.uid);

        // Filter to only show campaigns from other users
        const onlyShared = sharedCampaigns.filter(
          (campaign) => !campaign.isOwner
        );

        setCampaigns(onlyShared);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching shared campaigns:", err);
        setError("Failed to load shared campaigns. Please try again later.");
        setLoading(false);
      }
    };

    fetchSharedCampaigns();
  }, []);

  // Calculate time difference for display
  const getTimeAgo = (timestamp) => {
    if (!timestamp) return "Unknown date";

    // Convert Firebase timestamp to Date object if needed
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) {
      return `${diffInSeconds} seconds ago`;
    }

    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) {
      return `${diffInMinutes} ${
        diffInMinutes === 1 ? "minute" : "minutes"
      } ago`;
    }

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) {
      return `${diffInHours} ${diffInHours === 1 ? "hour" : "hours"} ago`;
    }

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 30) {
      return `${diffInDays} ${diffInDays === 1 ? "day" : "days"} ago`;
    }

    const diffInMonths = Math.floor(diffInDays / 30);
    return `${diffInMonths} ${diffInMonths === 1 ? "month" : "months"} ago`;
  };

  // Get access level icon
  const getAccessLevelIcon = (accessLevel) => {
    switch (accessLevel) {
      case "read":
        return <FaEye className="text-blue-500" title="View access" />;
      case "edit":
        return <FaEdit className="text-green-500" title="Edit access" />;
      case "admin":
        return <FaShareAlt className="text-purple-500" title="Admin access" />;
      default:
        return <FaLock className="text-gray-500" title="Unknown access" />;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded my-4">
        <p>{error}</p>
      </div>
    );
  }

  if (campaigns.length === 0) {
    return (
      <div className="bg-gray-50 rounded-lg p-6 text-center">
        <p className="text-gray-600">No shared campaigns available.</p>
        <p className="text-gray-500 text-sm mt-2">
          When someone shares an AI SDR campaign with you, it will appear here.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200">
        <h2 className="text-lg font-medium text-gray-900">Shared Campaigns</h2>
        <p className="mt-1 text-sm text-gray-500">
          Campaigns that have been shared with you by your teammates
        </p>
      </div>

      <ul className="divide-y divide-gray-200">
        {campaigns.map((campaign) => (
          <li
            key={`${campaign.ownerUid}-${campaign.id}`}
            className="hover:bg-gray-50"
          >
            <Link
              to={`/ai-sdr/campaign-viewer/${campaign.ownerUid}/${campaign.id}`}
              className="block px-6 py-4"
            >
              <div className="flex justify-between items-start">
                <div>
                  <div className="flex items-center">
                    <h3 className="text-lg font-medium text-gray-900">
                      {campaign.name}
                    </h3>
                    <span className="ml-3 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                      {getAccessLevelIcon(campaign.accessLevel)}
                      <span className="ml-1">{campaign.accessLevel}</span>
                    </span>
                  </div>

                  <p className="mt-1 text-sm text-gray-500">
                    {campaign.description}
                  </p>

                  <div className="mt-2 flex flex-wrap gap-2">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {campaign.leadCount || 0} leads
                    </span>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      Goal: {campaign.goal}
                    </span>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                      Status: {campaign.status}
                    </span>
                  </div>
                </div>

                <div className="text-right flex flex-col items-end">
                  <div className="flex items-center text-sm text-gray-500">
                    <FaUserCircle className="mr-1" />
                    {campaign.sellerInfo?.name || "Unknown owner"}
                  </div>
                  <div className="text-xs text-gray-400 mt-1">
                    {campaign.createdAt
                      ? getTimeAgo(campaign.createdAt)
                      : "Unknown date"}
                  </div>
                </div>
              </div>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default SharedCampaignsList;
